import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { oxygenType, queryParamsType } from '../../constants/proptype-validation';
import T from 'i18n-react';
import { preloadedFilterQueryParamsState } from '../../constants/preloaded-states';
import { toIsoDate } from '../../utils/date-helpers';
import onClickOutside from 'react-onclickoutside';
import {
  DatePicker,
  Checkbox,
  Label,
} from '@adsk/bim360-matrix-react-components';
import { ISSUE_FILTER_PROPERTIES, PAGE_PARAMS } from '../../constants/issue-query-params';
import { getAssignees } from '../../utils/assigned-to-helpers';
import { viewTypes } from '../../constants/view-types';
import { getUserCompanyName } from '../../utils/userHelpers';
import StatusDropDown from './components/StatusDropDown';
import CreatedByDropDown from './components/CreatedByDropDown';
import CostDropDown from './components/CostDropDown';
import AssignToDropDown from './components/AssignToDropDown';
import CategoryDropDown from './components/CategoryDropDown';
import ScheduleDropDown from './components/ScheduleDropDown';
import DisciplineDropDown from './components/DisciplineDropDown';
import PriorityDropDown from './components/PriorityDropDown';

function split(params) {
  if (params && params.length) {
    return params.split(',');
  }

  return null;
}

class Filter extends PureComponent {
  constructor(props) {
    super(props);

    this.state = this.initialQueryState(props.queryParams);
    this.filterDiv = React.createRef();
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.renderFilterField = this.renderFilterField.bind(this);
    this.getSelectedOptions = this.getSelectedOptions.bind(this);
    this.scrollIfNeeded = this.scrollIfNeeded.bind(this);
    this.filters = [
      ISSUE_FILTER_PROPERTIES.STATUS_FILTER,
      ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER,
      ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER,
      ISSUE_FILTER_PROPERTIES.SEPARATOR,
      ISSUE_FILTER_PROPERTIES.COST_IMPACT,
      ISSUE_FILTER_PROPERTIES.SCHEDULE_IMPACT,
      ISSUE_FILTER_PROPERTIES.PRIORITY,
      ISSUE_FILTER_PROPERTIES.SEPARATOR,
      ISSUE_FILTER_PROPERTIES.DISCIPLINE,
      ISSUE_FILTER_PROPERTIES.CATEGORY,
      ISSUE_FILTER_PROPERTIES.SPEC_SECTION,
      ISSUE_FILTER_PROPERTIES.SEPARATOR,
      ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER,
      ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER,
    ];
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.queryParams !== nextProps.queryParams) {
      this.setState(this.initialQueryState(nextProps.queryParams));
    }
  }

  initialQueryState(queryParams = []) {
    return {
      [ISSUE_FILTER_PROPERTIES.STATUS_FILTER]: queryParams[
        ISSUE_FILTER_PROPERTIES.STATUS_FILTER
      ].slice(0),
      [ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER]: getAssignees(
        queryParams[ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER],
        this.props.oxygen
      ),
      [ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER]: getAssignees(
        queryParams[ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER],
        this.props.oxygen
      ),
      [ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER]:
        queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER],
      [ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]:
        queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER],
      [ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER]:
        queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER],
      [ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]:
        queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER],
      [ISSUE_FILTER_PROPERTIES.INCLUDE_HIDDEN_FILTER]:
        queryParams[ISSUE_FILTER_PROPERTIES.INCLUDE_HIDDEN_FILTER],
      [ISSUE_FILTER_PROPERTIES.SEARCH]: queryParams[ISSUE_FILTER_PROPERTIES.SEARCH],
      [ISSUE_FILTER_PROPERTIES.COST_IMPACT]: split(queryParams[ISSUE_FILTER_PROPERTIES.COST_IMPACT]),
      [ISSUE_FILTER_PROPERTIES.SCHEDULE_IMPACT]:
        split(queryParams[ISSUE_FILTER_PROPERTIES.SCHEDULE_IMPACT]),
      [ISSUE_FILTER_PROPERTIES.CATEGORY]: split(queryParams[ISSUE_FILTER_PROPERTIES.CATEGORY]),
      [ISSUE_FILTER_PROPERTIES.DISCIPLINE]: split(queryParams[ISSUE_FILTER_PROPERTIES.DISCIPLINE]),
      [ISSUE_FILTER_PROPERTIES.PRIORITY]: split(queryParams[ISSUE_FILTER_PROPERTIES.PRIORITY]),
    };
  }

  getPreloadedQueryParamsState() {
    return preloadedFilterQueryParamsState[this.props.viewType];
  }

  handleClickOutside() {
    this.props.displayFilterDropdown(false);
  }

  updateFilterField(key, fn = x => x) {
    return value => {
      this.setState(
        { [key]: fn(value) },
        this.onFilterUpdate
      );
    };
  }

  updateFilterDateField(startDateKey, endDateKey) {
    return date => {
      const startDate = date ? toIsoDate(date.startDate) : '';
      const endDate = date ? toIsoDate(date.endDate) : '';
      this.updateFilterField(startDateKey)(startDate);
      this.updateFilterField(endDateKey)(endDate);
    };
  }

  handleSubmit() {
    const { setQueryParamsAction } = this.props;

    setQueryParamsAction({
      [PAGE_PARAMS.OFFSET]: 0,
      [ISSUE_FILTER_PROPERTIES.STATUS_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.STATUS_FILTER],
      [ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER]: this.state[
        ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER
      ].map(user => user.oxygenId),
      [ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER]: this.state[
        ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER
      ].map(x => x.oxygenId),
      [ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER]: this.state[
        ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER
      ],
      [ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]: this.state[
        ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER
      ],
      [ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER]: this.state[
        ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER
      ],
      [ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]: this.state[
        ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER
      ],
      [ISSUE_FILTER_PROPERTIES.INCLUDE_HIDDEN_FILTER]: this.state[
        ISSUE_FILTER_PROPERTIES.INCLUDE_HIDDEN_FILTER
      ],
      [ISSUE_FILTER_PROPERTIES.COST_IMPACT]: this.state[ISSUE_FILTER_PROPERTIES.COST_IMPACT] &&
        this.state[ISSUE_FILTER_PROPERTIES.COST_IMPACT].join(','),
      [ISSUE_FILTER_PROPERTIES.SCHEDULE_IMPACT]: this.state[ISSUE_FILTER_PROPERTIES.SCHEDULE_IMPACT] &&
        this.state[ISSUE_FILTER_PROPERTIES.SCHEDULE_IMPACT].join(','),
      [ISSUE_FILTER_PROPERTIES.CATEGORY]:
        this.state[ISSUE_FILTER_PROPERTIES.CATEGORY] &&
        this.state[ISSUE_FILTER_PROPERTIES.CATEGORY].join(','),
      [ISSUE_FILTER_PROPERTIES.DISCIPLINE]:
        this.state[ISSUE_FILTER_PROPERTIES.DISCIPLINE] &&
        this.state[ISSUE_FILTER_PROPERTIES.DISCIPLINE].join(','),
      [ISSUE_FILTER_PROPERTIES.PRIORITY]: this.state[ISSUE_FILTER_PROPERTIES.PRIORITY] &&
        this.state[ISSUE_FILTER_PROPERTIES.PRIORITY].join(','),
    });
  }

  onFilterUpdate() {
    this.handleSubmit();
  }

  handleReset() {
    const { setQueryParamsAction, displayFilterDropdown } = this.props;

    this.setState(this.getPreloadedQueryParamsState());
    setQueryParamsAction(this.getPreloadedQueryParamsState());

    displayFilterDropdown(false);
  }

  renderCreatedAtDropdown({ disabled, key }) {
    const startDateKey = ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER;
    const endDateKey = ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER;
    return (
      <div className="Filter__field" key={key}>
        <Label className="Filter__field-label">{T.translate('filter.createdAt')}</Label>
        <DatePicker
          placeholder={T.translate('filter.dueDatePlaceholder')}
          range
          startDate={this.state[startDateKey] || null}
          endDate={this.state[endDateKey] || null}
          onChange={this.updateFilterDateField(startDateKey, endDateKey)}
          onOpen={this.scrollIfNeeded}
          disabled={disabled}
        />
      </div>
    );
  }

  renderShowHiddenCheckbox() {
    const fieldKey = ISSUE_FILTER_PROPERTIES.INCLUDE_HIDDEN_FILTER;
    return (
      <div className="Filter__field">
        <Checkbox
          onChange={this.updateFilterField(fieldKey)}
          checked={this.state[fieldKey]}
          label={T.translate('filter.showHidden')}
        />
      </div>
    );
  }

  renderDueDateFilter({ disabled, key }) {
    /* Due Date range */
    const startDateKey = ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER;
    const endDateKey = ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER;
    return (
      <div className="Filter__field" key={key}>
        <Label className="Filter__field-label">{T.translate('new.dueDate')}</Label>
        <DatePicker
          placeholder={T.translate('filter.dueDatePlaceholder')}
          range
          startDate={this.state[startDateKey] || null}
          endDate={this.state[endDateKey] || null}
          onChange={this.updateFilterDateField(startDateKey, endDateKey)}
          disabled={disabled}
        />
      </div>
    );
  }

  getSelectedOptions(values, options) {
    const asArray = Array.isArray(values) ? values : [values];
    return options.filter(({ key }) => {
      return asArray.includes(key);
    });
  }

  scrollIfNeeded() {
    if (this.filterDiv.current) {
      this.filterDiv.current.parentNode.scrollTop = this.filterDiv.current.parentNode.scrollHeight;
    }
  }

  renderSeparator(key) {
    return <div key={key} className="Filter__separator" />;
  }

  renderFilterField(field, index) {
    const { disabled, projectUsers, impactOptions } = this.props;
    const fieldsProps = { disabled, key: field };
    const { STATUS_FILTER, ASSIGNED_TO_FILTER, CREATED_BY_FILTER, SCHEDULE_IMPACT, COST_IMPACT, PRIORITY, CATEGORY, DISCIPLINE } = ISSUE_FILTER_PROPERTIES;

    switch (field) {
      case STATUS_FILTER:
        return <StatusDropDown
          {...fieldsProps}
          selectedStatuses={this.state[ISSUE_FILTER_PROPERTIES.STATUS_FILTER]}
          onChange={this.updateFilterField(STATUS_FILTER, statuses => statuses.map(({ value }) => value))}
        />;
      case ASSIGNED_TO_FILTER:
        return <AssignToDropDown
          {...fieldsProps}
          options={projectUsers}
          selectedOptions={this.state[ASSIGNED_TO_FILTER]}
          subtextGetter={getUserCompanyName}
          onChange={this.updateFilterField(ASSIGNED_TO_FILTER)}
        />;
      case CREATED_BY_FILTER:
        return <CreatedByDropDown
          {...fieldsProps}
          options={projectUsers}
          selectedOptions={this.state[CREATED_BY_FILTER]}
          subtextGetter={getUserCompanyName}
          onChange={this.updateFilterField(CREATED_BY_FILTER)}
        />;
      case ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER:
        return this.renderCreatedAtDropdown({ ...fieldsProps });
      case ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER:
        return this.renderDueDateFilter({ ...fieldsProps });
      case SCHEDULE_IMPACT:
        const { schedule_impact } = this.state;
        return <ScheduleDropDown
          {...fieldsProps}
          selectedOptions={this.getSelectedOptions(schedule_impact, impactOptions)}
          options={impactOptions}
          onChange={this.updateFilterField(SCHEDULE_IMPACT, i => i.map(({ key }) => key))}
        />;
      case COST_IMPACT:
        const { cost_impact } = this.state;
        return <CostDropDown
          {...fieldsProps}
          selectedOptions={this.getSelectedOptions(cost_impact, impactOptions)}
          options={impactOptions}
          onChange={this.updateFilterField(COST_IMPACT, i => i.map(({ key }) => key))}
        />;
      case PRIORITY:
        const { priority } = this.state;
        const { priorityOptions } = this.props;
        return <PriorityDropDown
          {...fieldsProps}
          selectedOptions={this.getSelectedOptions(priority, priorityOptions)}
          options={priorityOptions}
          onChange={this.updateFilterField(PRIORITY, priorities => priorities.map(({ key }) => key))}
        />;
      case CATEGORY:
        const { category } = this.state;
        const { categoryOptions } = this.props;
        return <CategoryDropDown
          {...fieldsProps}
          selectedOptions={this.getSelectedOptions(category, categoryOptions)}
          options={categoryOptions}
          onChange={this.updateFilterField(CATEGORY, categories => categories.map(({ key }) => key))}
        />;
      case DISCIPLINE:
        const { discipline } = this.state;
        const { disciplineOptions } = this.props;
        return <DisciplineDropDown
          {...fieldsProps}
          selectedOptions={this.getSelectedOptions(discipline, disciplineOptions)}
          options={disciplineOptions}
          onChange={this.updateFilterField(DISCIPLINE, disciplines => disciplines.map(({ key }) => key))}
        />;
      case ISSUE_FILTER_PROPERTIES.SEPARATOR:
        return this.renderSeparator(index);
      default:
        return null;
    }
  }

  getFilter() {
    const { viewType } = this.props;
    return (
      <div className="Filter" ref={this.filterDiv}>
        {/*If you add more fields in to filter remember to change the height in the css class: '$issues-panel-header-height'*/}
        {this.filters.map(this.renderFilterField)}
        {/* Hidden RFIs */}
        {viewType === viewTypes.DOCUMENT && this.renderShowHiddenCheckbox()}
      </div>
    );
  }

  render() {
    return this.props.visible ? this.getFilter() : null;
  }
}

Filter.displayName = 'Filter';

Filter.propTypes = {
  visible: PropTypes.bool.isRequired,
  projectUsers: PropTypes.array.isRequired,
  disabled: PropTypes.bool.isRequired,
  oxygen: oxygenType.isRequired,
  setQueryParamsAction: PropTypes.func.isRequired,
  queryParams: queryParamsType.isRequired,
  displayFilterDropdown: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  impactOptions: PropTypes.array.isRequired,
  priorityOptions: PropTypes.array.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  disciplineOptions: PropTypes.array.isRequired,
};

export default onClickOutside(Filter);
